import { create } from "zustand";
import type { SyncFailureKind } from "./syncFailure";

export type EncryptedSyncPhase = "idle" | "syncing" | "ready" | "attention";

interface EncryptedSyncStatus {
  phase: EncryptedSyncPhase;
  pendingUploads: number;
  pendingDownloads: number;
  failedAttempts: number;
  failureKinds: SyncFailureKind[];
  lastCheckedAt: string | null;
  lastCompletedAt: string | null;
  setSyncing: () => void;
  setResult: (result: { pendingUploads: number; pendingDownloads: number; failedAttempts: number; failureKinds?: SyncFailureKind[] }) => void;
  setAttention: (failureKind?: SyncFailureKind) => void;
  reset: () => void;
}

const initial = {
  phase: "idle" as const,
  pendingUploads: 0,
  pendingDownloads: 0,
  failedAttempts: 0,
  failureKinds: [],
  lastCheckedAt: null,
  lastCompletedAt: null
};

export const useEncryptedSyncStatus = create<EncryptedSyncStatus>((set) => ({
  ...initial,
  setSyncing: () => set((state) => ({ ...state, phase: "syncing" })),
  setResult: (result) => {
    const now = new Date().toISOString();
    const failureKinds = [...new Set(result.failureKinds ?? [])];
    return set({
      phase: result.failedAttempts > 0 ? "attention" : "ready",
      pendingUploads: result.pendingUploads,
      pendingDownloads: result.pendingDownloads,
      failedAttempts: result.failedAttempts,
      failureKinds,
      lastCheckedAt: now,
      lastCompletedAt: now
    });
  },
  setAttention: (failureKind = "unknown") => set((state) => ({
    ...state,
    phase: "attention",
    failureKinds: [failureKind],
    lastCheckedAt: new Date().toISOString()
  })),
  reset: () => set(initial)
}));

export function encryptedSyncStatusLabel(status: Pick<EncryptedSyncStatus, "phase" | "pendingUploads" | "pendingDownloads" | "failedAttempts">) {
  if (status.phase === "syncing") return "正在安全同步";
  if (status.phase === "attention") return "需要重试";
  if (status.pendingUploads + status.pendingDownloads > 0) return "等待同步";
  if (status.phase === "ready") return "已检查";
  return "等待登录";
}
